/**
 * `<AmountText>` — the shared money renderer, and the place new code should
 * put a rupee figure.
 *
 * It exists for two reasons, and only one of them is privacy:
 *
 *  1. It is the going-forward home for "render paise as INR", so the app stops
 *     growing a twenty-ninth local `Intl.NumberFormat` helper.
 *  2. It carries hide-values (V5-03) as MARKUP rather than as a string, which
 *     lets it do two things a formatter cannot: expose the real value to
 *     assistive tech only when it is not hidden, and offer a per-element
 *     reveal.
 *
 * WHY THE FORMATTERS ARE ALSO MASKED, NOT JUST THIS COMPONENT
 * ------------------------------------------------------------
 * Because 71 files already call `formatINR(paise)` inline in JSX and this
 * component did not exist when they were written. Rewriting all of them into
 * `<AmountText>` is a codemod across screens four other agents are editing
 * right now. So hide-values is implemented at the FORMATTER layer
 * (`lib/utils.ts` reads the privacy store), which covers every existing screen,
 * and this component is what the next screen should use. The two agree because
 * they call the same `maskMoney`.
 *
 * MASKING NEVER TOUCHES THE NUMBER
 * ---------------------------------
 * `paise` goes in, `paise` stays. Totals, charts, sums and request bodies are
 * computed from the number and never from this string, so hiding a figure
 * cannot change a balance.
 */
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { formatAmountRaw } from "@core";
import { HIDDEN_AMOUNT } from "@/lib/privacy/mask";
import { usePrivacy } from "@/lib/privacy/store";

/**
 * The unmasked formatters. Deliberately NOT `formatINR` from `lib/utils`:
 * that one masks by itself (that is the whole point of it), and calling it
 * here would mask twice and make the `hidden` branch below unreachable and
 * untestable. This component decides, once, and formats accordingly.
 */
function formatFull(paise: number): string {
  // Exact: ₹2,500.45, and ₹2,500 when whole. Never rounds paise away.
  return formatAmountRaw(paise);
}

export interface AmountTextProps {
  /** The amount, in paise. Always paise — see the repo-wide convention. */
  paise: number;
  /**
   * Formerly `1.2L` / `36L` / `1.5Cr`. Kept so existing call sites compile,
   * but it no longer changes the figure: a displayed amount is always EXACT
   * (owner, 2026-09-28: "₹2500.45 like this no round off"). Compact notation
   * survives only on chart axes, which are a scale, not a value.
   */
  compact?: boolean;
  /**
   * Render this amount even when hide-values is on.
   *
   * For the rare figure that is not sensitive but is money-shaped — a tax slab
   * ceiling, an 80C limit, a printed statutory threshold. Off by default: an
   * opt-out that is easy to reach is an opt-out that ends up on the dashboard.
   */
  alwaysVisible?: boolean;
  className?: string;
  /** Rendered instead of `₹0` when `paise` is null/undefined/NaN. */
  fallback?: string;
}

export function AmountText({
  paise,
  alwaysVisible = false,
  className,
  fallback = "—",
}: AmountTextProps) {
  const { hideValues } = usePrivacy();
  const valid = typeof paise === "number" && Number.isFinite(paise);

  const formatted = useMemo(
    () => (valid ? formatFull(paise) : fallback),
    [paise, valid, fallback],
  );

  if (!valid) return <span className={className}>{fallback}</span>;

  const hidden = hideValues && !alwaysVisible;

  return (
    <span
      className={cn("tabular-nums", hidden && "select-none tracking-[0.12em]", className)}
      // The real figure is announced only when it is on screen. A screen
      // reader reading out "twelve lakh forty-five thousand" while the display
      // shows bullets would defeat the feature in the exact room it is for.
      aria-label={hidden ? "Amount hidden" : formatted}
      title={hidden ? "Amounts are hidden — use the eye button in the top bar" : undefined}
      data-hidden={hidden ? "true" : undefined}
    >
      {hidden ? HIDDEN_AMOUNT : formatted}
    </span>
  );
}
